import { Node, Position } from '@xyflow/react';

import { CIRCLE_RADIUS } from '../utils';

export function getEdgePosition(nodeA: Node, nodeB: Node): Position {
  const nodeABounds = getNodeBounds(nodeA);
  const nodeBBounds = getNodeBounds(nodeB);

  const centerA = {
    x: nodeABounds.x + nodeABounds.width / 2,
    y: nodeABounds.y + nodeABounds.height / 2,
  };

  const centerB = {
    x: nodeBBounds.x + nodeBBounds.width / 2,
    y: nodeBBounds.y + nodeBBounds.height / 2,
  };

  const horizontalDiff = Math.abs(centerA.x - centerB.x);
  const verticalDiff = Math.abs(centerA.y - centerB.y);

  if (horizontalDiff > verticalDiff) {
    return centerA.x > centerB.x ? Position.Left : Position.Right;
  } else {
    return centerA.y > centerB.y ? Position.Top : Position.Bottom;
  }
}

export function getNodeIntersection(node: Node, position: Position): { x: number; y: number } {
  const bounds = getNodeBounds(node);

  switch (position) {
    case Position.Left:
      return {
        x: bounds.x - CIRCLE_RADIUS,
        y: bounds.y + bounds.height / 2,
      };
    case Position.Right:
      return {
        x: bounds.x + bounds.width + CIRCLE_RADIUS,
        y: bounds.y + bounds.height / 2,
      };
    case Position.Top:
      return {
        x: bounds.x + bounds.width / 2,
        y: bounds.y,
      };
    case Position.Bottom:
      return {
        x: bounds.x + bounds.width / 2,
        y: bounds.y + bounds.height,
      };
    default:
      return {
        x: bounds.x + bounds.width / 2,
        y: bounds.y + bounds.height / 2,
      };
  }
}

function getNodeBounds(node: Node): { x: number; y: number; width: number; height: number } {
  const getDefaultDimensions = (nodeType?: string): { width: number; height: number } => {
    if (nodeType === 'condition') {
      return { width: 248, height: 92 };
    }
    if (nodeType === 'for_loop') {
      return { width: 320, height: 188 };
    }
    return { width: 248, height: 108 };
  };

  const defaults = getDefaultDimensions(node.type);

  let width: number;
  let height: number;

  if (node.measured?.width && node.measured?.height) {
    width = node.measured.width;
    height = node.measured.height;
  } else if (node.width && node.height) {
    width = typeof node.width === 'string' ? parseFloat(node.width) : node.width;
    height = typeof node.height === 'string' ? parseFloat(node.height) : node.height;
  } else if (node.style?.width && node.style?.height) {
    const styleWidth = node.style.width;
    const styleHeight = node.style.height;
    width = typeof styleWidth === 'string' ? parseFloat(styleWidth) : styleWidth;
    height = typeof styleHeight === 'string' ? parseFloat(styleHeight) : styleHeight;
  } else {
    width = defaults.width;
    height = defaults.height;
  }

  const nodeInternal = node as Node & {
    internals?: {
      positionAbsolute?: { x: number; y: number };
    };
  };

  const absolutePosition = nodeInternal.internals?.positionAbsolute || node.position;

  return {
    x: absolutePosition.x,
    y: absolutePosition.y,
    width,
    height,
  };
}

export function getEdgeParams(source: Node, target: Node) {
  const sourcePosition = getEdgePosition(source, target);
  const targetPosition = getEdgePosition(target, source);

  const sourceIntersection = getNodeIntersection(source, sourcePosition);
  const targetIntersection = getNodeIntersection(target, targetPosition);

  return {
    sourceX: sourceIntersection.x,
    sourceY: sourceIntersection.y,
    targetX: targetIntersection.x,
    targetY: targetIntersection.y,
    sourcePosition,
    targetPosition,
  };
}
